import { createFilterOptions } from "@mui/material/Autocomplete";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  InputAdornment,
  IconButton,
  MenuItem,
  Modal,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Spinner } from "../../../components/Spinner/Spinner";
import { useLanguages } from "../../../context/LanguagesContext";
import { usePortal } from "../../../context/PortalsContext";
import { useToast } from "../../../context/ToastContext";
import {
  createNewUser,
  updateUser,
  getPortalUserAttribute,
  getUserPortalMappingsByEmail,
  getUserWithAllDetails,
  getRolesForUser,
} from "../../../services/api/apiClient";
import {
  AttributeType,
  CreateUserBodyParams,
  UpdateUserBodyParams,
  GetPortalUserAttributeResult,
  Roles,
  UserPortalMapping,
  UserStatusType,
  GetRolesForUserResult,
} from "../../../services/api/types/users/users";
import { capitalizeFirstLetter, isValidEmail } from "../../../utils/helpers";
import {
  ASSIGNABLE_ROLES_PORTAL_CONFIG_ORDER,
  getRolesNotInAssignableSet,
  sortRolesByPortalConfigOrder,
} from "../../../utils/roleUtils";
import { DynamicAttributeField } from "./components/DynamicAttributeField";

import CloseIcon from "@mui/icons-material/Close";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { ConfirmModal } from "../../../components/ConfirmModal/ConfirmModal";
import { usePageMetadata } from "../../../context/PageMetadataContext";
import { brand, gray } from "../../../theme/themePrimitives";
import { ReactComponent as HubIcon } from "../../../assets/hub.svg";
import { ReactComponent as PortalIcon } from "../../../assets/portal.svg";

interface AddNewUserProps {}

const ITEMS_PER_PAGE = 6;
const rolesFilter = createFilterOptions<Roles>();
type RolesAutocompleteFilterState = Parameters<
  ReturnType<typeof createFilterOptions<Roles>>
>[1];

export type AttributeValueType = string | number | boolean | string[] | null;

const toApiString = (v: AttributeValueType): string | null => {
  if (v === "" || v === null || v === undefined) return null;
  if (Array.isArray(v)) return v.join("|"); // Handle arrays (shouldn't happen in normalize, but just in case)
  if (typeof v === "boolean") return v ? "true" : "false";
  return String(v);
};

export const AddNewUser: React.FC<AddNewUserProps> = () => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [roles, setRoles] = useState<Roles[]>([]);
  const [initialStatus, setInitialStatus] = useState<UserStatusType | "">("");
  const [status, setStatus] = useState<UserStatusType | "">("");
  const [statusTypes, setStatusTypes] = useState<UserStatusType[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [visibleCount, setVisibleCount] = useState(ITEMS_PER_PAGE);
  const [loadingAttributes, setLoadingAttributes] = useState(false);
  const [additionalAttributesResp, setAdditionalAttributesResp] = useState<
    GetPortalUserAttributeResult[]
  >([]);
  const [attributeValues, setAttributeValues] = useState<
    Record<string, AttributeValueType>
  >({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isEditMode, setIsEditMode] = useState(false);
  const isArchivedUser =
    isEditMode && initialStatus === UserStatusType.ARCHIVED;
  const [editUserPortalUid, setEditUserPortalUid] = useState<string | null>(
    null,
  );
  const [showArchivingModal, setShowArchivingModal] = useState(false);
  const [pendingArchiveSubmit, setPendingArchiveSubmit] = useState(false);
  const [openFormDatepickerKey, setOpenFormDatepickerKey] = useState<
    string | null
  >(null);
  const [emailPortalMappings, setEmailPortalMappings] = useState<
    UserPortalMapping[] | null
  >(null);
  const [isPortalPreviewOpen, setIsPortalPreviewOpen] = useState(false);
  const [rolesApiResult, setRolesApiResult] =
    useState<GetRolesForUserResult | null>(null);
  const [loadingRoles, setLoadingRoles] = useState(true);

  const { languages } = useLanguages();
  const { portalUid } = useParams();
  const { portals } = usePortal();
  const { showToast } = useToast();
  const { state } = useLocation();
  const navigate = useNavigate();

  // Roles the current admin can assign (not disabled by role level).
  // If editing a specific user and the API returned an override set, use that.
  const assignableOptions = useMemo(
    () =>
      rolesApiResult?.roles
        .filter((r) => !r.isDisabled)
        .map((r) => r.role as Roles) ?? ASSIGNABLE_ROLES_PORTAL_CONFIG_ORDER,
    [rolesApiResult],
  );

  // Roles the user already has that the current admin cannot touch.
  const lockedRoles = useMemo(
    () => getRolesNotInAssignableSet(roles, assignableOptions),
    [roles, assignableOptions],
  );

  // All options to render in the dropdown list (full API list + any locked chips).
  const roleAutocompleteOptions = useMemo(() => {
    const allRoles =
      rolesApiResult?.roles.map((r) => r.role as Roles) ??
      ASSIGNABLE_ROLES_PORTAL_CONFIG_ORDER;
    return Array.from(new Set([...allRoles, ...lockedRoles])) as Roles[];
  }, [rolesApiResult, lockedRoles]);

  // Text-search filter: show all roles (including disabled ones that appear greyed-out),
  // but exclude locked roles (they are already rendered as non-removable chips).
  const filterRoleOptions = useMemo(
    () => (options: Roles[], state: RolesAutocompleteFilterState) => {
      const shown = rolesFilter(options, state);
      return shown.filter((o) => !lockedRoles.includes(o));
    },
    [lockedRoles],
  );

  // Whether a single option in the dropdown should be greyed out.
  const isRoleOptionDisabled = useCallback(
    (role: Roles): boolean => {
      if (!rolesApiResult) return false;
      return (
        rolesApiResult.roles.find((r) => r.role === role)?.isDisabled ?? false
      );
    },
    [rolesApiResult],
  );

  // Whether the entire dropdown should be disabled (LVL4 and below).
  const isDropdownDisabled = rolesApiResult?.dropdownDisabled ?? false;

  const fetchUserWithAllDetails = async (UserPortalUid: string) => {
    if (!portalUid) return;
    if (!UserPortalUid) return;

    try {
      const response = await getUserWithAllDetails(portalUid, UserPortalUid);

      setEditUserPortalUid(response.UserPortalUid ?? null);
      setFirstName(response.firstName ?? "");
      setLastName(response.lastName ?? "");
      setEmail(response.email ?? "");
      setRoles(response.roles ?? []);
      setInitialStatus(response.status ?? "");
      setStatus(response.status ?? "");
      setStatusTypes(response.statusTypes ?? []);
      setEmailPortalMappings(
        response.portals && response.portals.length > 0
          ? response.portals
          : null,
      );

      if (response.attributes?.length) {
        const mappedAttrs: Record<string, AttributeValueType> = {};

        response.attributes.forEach((attr: any) => {
          // Find the attribute definition to check if it's isLineManager
          const attrDef = additionalAttributesResp.find(
            (a) => a.attributeKey === attr.attributeKey,
          );

          // If it's isLineManager and string type, parse pipe-separated string to array
          if (
            attrDef?.isLineManager &&
            attrDef.dataType === "string" &&
            typeof attr.attributeValue === "string" &&
            attr.attributeValue
          ) {
            mappedAttrs[attr.attributeKey] = attr.attributeValue
              .split("|")
              .filter(Boolean);
          } else {
            mappedAttrs[attr.attributeKey] = attr.attributeValue ?? null;
          }
        });

        setAttributeValues(mappedAttrs);
      }
    } catch (error) {
      setEmailPortalMappings(null);
      console.log("ERROR", error);
    }
  };

  const handleChange = (
    value: string | Roles[],
    elementName: string,
    elementText: string,
  ) => {
    if (typeof value === "object") {
      if (value.length > 0) {
        clearError(elementName);
      } else {
        setErrors((prev) => ({
          ...prev,
          [elementName]: `${elementText} is required`,
        }));
      }
    } else if (value.trim()) {
      clearError(elementName);
    } else {
      setErrors((prev) => ({
        ...prev,
        [elementName]: `${elementText} is required`,
      }));
    }
  };

  const fetchPortalUserAttribute = async () => {
    if (!portalUid) return;
    setLoadingAttributes(true);

    try {
      const response = await getPortalUserAttribute(
        portalUid,
        languages.selectedLanguage,
        true,
      );
      setAdditionalAttributesResp(response.rows ?? []);
    } catch (error) {
      console.log("ERROR: ", error);
    } finally {
      setLoadingAttributes(false);
    }
  };

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        if (!portalUid) return;
        setLoadingRoles(true);
        const response = await getRolesForUser(portalUid);
        setRolesApiResult(response);
      } catch (error: any) {
        console.log("Error:", error);
        showToast(error?.response?.data ?? "Failed to load roles", "error");
      } finally {
        setLoadingRoles(false);
      }
    };
    fetchRoles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portalUid]);

  useEffect(() => {
    if (state && state.UserPortalUid) {
      setIsEditMode(true);
      fetchUserWithAllDetails(state.UserPortalUid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  useEffect(() => {
    fetchPortalUserAttribute();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portalUid]);

  useEffect(() => {
    setVisibleCount(() =>
      Math.min(ITEMS_PER_PAGE, additionalAttributesResp?.length),
    );
  }, [additionalAttributesResp.length]);

  const submitUser = async () => {
    if (!portalUid) {
      console.warn("Missing portalUid");
      return;
    }
    const isValid = validateFields();
    if (!isValid) return;

    try {
      setSubmitting(true);

      // In create mode - always send REGISTERED status
      // In edit mode - whatever is selected in the status field (fallback to initialStatus)
      const nextStatus: UserStatusType = isEditMode
        ? ((status || initialStatus) as UserStatusType)
        : UserStatusType.REGISTERED;

      if (isEditMode) {
        if (!editUserPortalUid) {
          return;
        }
        const updateBody: UpdateUserBodyParams = {
          UserPortalUid: editUserPortalUid,
          status: nextStatus,
          firstName,
          lastName,
          roles,
          additionalAttributes: normalizeAdditionalAttributesForRequest(),
        };
        await updateUser(portalUid, updateBody);
        showToast("Successfully updated user", "success");
      } else {
        const createBody: CreateUserBodyParams = {
          email,
          status: nextStatus,
          firstName,
          lastName,
          roles,
          additionalAttributes: normalizeAdditionalAttributesForRequest(),
        };
        await createNewUser(portalUid, createBody);
        showToast("Successfully added new user", "success");
      }

      setTimeout(() => {
        navigate(
          `/portal-configuration/${portalUid}/users`,
          isEditMode ? { state: { refreshFilter: true } } : undefined,
        );
      }, 2000);
    } catch (error: any) {
      showToast(error?.response?.data ?? "Failed to save user", "error");
      console.log("Error: ", error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCheckEmail = async (email: string) => {
    try {
      if (!email.trim()) {
        return;
      } else if (!isValidEmail(email)) {
        return;
      }
      const response = await getUserPortalMappingsByEmail(
        portals.selectedPortal?.portalUid!,
        { email },
      );
      setEmailPortalMappings(response);
    } catch (error: any) {
      showToast(error?.response?.data ?? "Failed to check email", "error");
      console.log("Error: ", error);
    }
  };

  const handleSubmit = async () => {
    if (!portalUid) return;

    const isValid = validateFields();
    if (!isValid) return;

    const nextStatus: UserStatusType = isEditMode
      ? ((status || initialStatus) as UserStatusType)
      : UserStatusType.REGISTERED;

    const shouldConfirmArchive =
      isEditMode &&
      initialStatus !== UserStatusType.ARCHIVED &&
      nextStatus === UserStatusType.ARCHIVED;

    if (shouldConfirmArchive) {
      setPendingArchiveSubmit(true);
      setShowArchivingModal(true);
      return;
    }

    await submitUser();
  };

  // Prepare the right format needed for the payload
  const normalizeAdditionalAttributesForRequest = (): AttributeType[] => {
    return additionalAttributesResp.map(
      (attr: GetPortalUserAttributeResult) => {
        const portalUserAttributeUid = attr.portalUserAttributeUid;
        const key = attr.attributeKey;
        const value = attributeValues[key];

        // Handle isLineManager string attributes (pipe separated)
        if (
          attr.isLineManager &&
          attr.dataType === "string" &&
          Array.isArray(value)
        ) {
          return {
            PortalUserAttributeUid: portalUserAttributeUid,
            attributeKey: key,
            dataType: attr.dataType,
            attributeOrder: attr.attributeOrder,
            attributeValue: value.join("|"),
            isRequired: attr.isRequired,
          };
        }

        return {
          PortalUserAttributeUid: portalUserAttributeUid,
          attributeKey: key,
          dataType: attr.dataType,
          attributeOrder: attr.attributeOrder,
          attributeValue: toApiString(value),
          isRequired: attr.isRequired,
        };
      },
    );
  };

  const setAttr = (key: string, value: AttributeValueType) => {
    setAttributeValues((s) => ({ ...s, [key]: value }));

    const attr = additionalAttributesResp.find(
      (a) => a.attributeKey === key,
    );

    if (hasValue(value) || !attr?.isRequired) {
      clearError(key);
    } else {
      setErrors((prev) => ({
        ...prev,
        [key]: `${capitalizeFirstLetter(
          attr["portalUserAttributeTranslations.attributeName"] ||
            attr.attributeName ||
            attr.attributeKey,
        )} is required.`,
      }));
    }
  };

  const handleExpand = () => {
    setVisibleCount((prev) =>
      prev + ITEMS_PER_PAGE > additionalAttributesResp.length
        ? additionalAttributesResp.length
        : prev + ITEMS_PER_PAGE,
    );
  };

  const handleCollapse = () => {
    setVisibleCount(ITEMS_PER_PAGE);
  };

  const visibleAttributes = additionalAttributesResp?.slice(0, visibleCount);

  const validateFields = (): boolean => {
    const newErrors: Record<string, string> = {};

    // The static fields
    if (!firstName.trim()) newErrors.firstName = "First name is required.";
    if (!lastName.trim()) newErrors.lastName = "Last name is required.";
    if (!email.trim()) {
      newErrors.email = "Email is required.";
    } else if (!isValidEmail(email)) {
      newErrors.email = "Enter a valid email.";
    }
    if (roles.length === 0) newErrors.roles = "At least one role is required.";

    // The dynamic fields
    additionalAttributesResp.forEach((attr) => {
      const key = attr.attributeKey;
      const isRequired = attr.isRequired;
      const value = attributeValues[key];

      if (isRequired && !hasValue(value)) {
        newErrors[key] = `${capitalizeFirstLetter(
          attr["portalUserAttributeTranslations.attributeName"] ||
            attr.attributeName ||
            attr.attributeKey,
        )} is required.`;
      } else {
        const emailError = getLineManagerEmailError(attr, value);
        if (emailError) newErrors[key] = emailError;
      }
    });

    setErrors(newErrors);

    // Return true if valid
    return Object.keys(newErrors).length === 0;
  };

  const getLineManagerEmailError = (
    attr: GetPortalUserAttributeResult,
    value: AttributeValueType,
  ): string | null => {
    if (!attr.isLineManager || attr.dataType !== "string") return null;

    const emails = Array.isArray(value)
      ? value
      : typeof value === "string" && value
        ? value
            .split("|")
            .map((v) => v.trim())
            .filter(Boolean)
        : [];

    if (emails.some((email) => !isValidEmail(email))) {
      return `Enter a valid email address for ${capitalizeFirstLetter(
        attr["portalUserAttributeTranslations.attributeName"] ||
          attr.attributeName ||
          attr.attributeKey,
      )}.`;
    }

    return null;
  };

  const hasValue = (value: AttributeValueType) => {
    if (!value) return false;
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === "boolean") return true;
    return String(value).trim() !== "";
  };

  const useClearError =
    (setErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>) =>
    (key: string) =>
      setErrors((prev) => {
        if (!prev[key]) return prev;
        const { [key]: _omit, ...rest } = prev;
        return rest;
      });

  const clearError = useClearError(setErrors);

  usePageMetadata(
    {
      title: isEditMode ? "Edit user" : "Add new user",
      breadcrumbs: [
        {
          label: "User management",
          path: `/portal-configuration`,
        },
        {
          label: "User search",
          path: `/portal-configuration/${portalUid}/users`,
        },
        {
          label: isEditMode ? "Edit user" : "Add new user",
        },
      ],
    },
    [portalUid, isEditMode],
  );

  return (
    <Box>
      <Paper sx={{ p: 2 }}>
        <Stack
          direction="row"
          spacing={2}
          divider={!isEditMode && <Divider orientation="vertical" flexItem />}
        >
          <Stack sx={{ flex: 1 }}>
            <Stack spacing={1}>
              <Typography variant="subtitle2" fontWeight="bold">
                First name
                <Box component="span" sx={{ color: `var(--error-700)` }}>
                  *
                </Box>
              </Typography>
              <TextField
                placeholder="Enter first name"
                value={firstName}
                onChange={(e) => {
                  const value = e.target.value;
                  setFirstName(value);
                  handleChange(value, "firstName", "First Name");
                }}
                required
                disabled={isArchivedUser}
                size="small"
                error={!!errors.firstName}
                helperText={errors.firstName || " "}
                slotProps={{
                  input: {
                    endAdornment: errors.firstName && (
                      <ErrorOutlineIcon color="error" fontSize="small" />
                    ),
                  },
                }}
                sx={{
                  "& .MuiInputBase-input": { px: "14x", py: "10px" },
                }}
              />
            </Stack>

            <Stack spacing={1}>
              <Typography variant="subtitle2" fontWeight="bold">
                Last name
                <Box component="span" sx={{ color: `var(--error-700)` }}>
                  *
                </Box>
              </Typography>
              <TextField
                placeholder="Enter last name"
                value={lastName}
                onChange={(e) => {
                  const value = e.target.value;
                  setLastName(value);
                  handleChange(value, "lastName", "Last Name");
                }}
                required
                disabled={isArchivedUser}
                size="small"
                error={!!errors.lastName}
                helperText={errors.lastName || " "}
                slotProps={{
                  input: {
                    endAdornment: errors.lastName && (
                      <ErrorOutlineIcon color="error" fontSize="small" />
                    ),
                  },
                }}
                sx={{
                  "& .MuiInputBase-input": { px: "14x", py: "10px" },
                }}
              />
            </Stack>
          </Stack>

          <Stack sx={{ flex: 1 }}>
            <Stack spacing={1}>
              <Typography variant="subtitle2" fontWeight="bold">
                Email
                <Box component="span" sx={{ color: `var(--error-700)` }}>
                  *
                </Box>
              </Typography>
              <TextField
                placeholder="Enter email address"
                value={email}
                onChange={(e) => {
                  const value = e.target.value;
                  setEmail(value);
                  handleChange(value, "email", "Email");
                  setEmailPortalMappings(null);
                }}
                onBlur={(e) => handleCheckEmail(e.target.value)}
                disabled={isEditMode}
                required
                size="small"
                error={!!errors.email}
                helperText={errors.email || " "}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={0.5}
                        >
                          {(emailPortalMappings?.length ?? 0) > 0 ? (
                            <IconButton
                              size="small"
                              sx={{
                                border: "none",
                                borderRadius: "8px",
                                width: 28,
                                height: 28,
                              }}
                              onClick={() => setIsPortalPreviewOpen(true)}
                            >
                              <HubIcon width={18} height={18} />
                            </IconButton>
                          ) : null}
                          {errors.email ? (
                            <ErrorOutlineIcon color="error" fontSize="small" />
                          ) : null}
                        </Stack>
                      </InputAdornment>
                    ),
                  },
                }}
                sx={{
                  "& .MuiInputBase-input": { px: "14x", py: "10px" },
                }}
              />
            </Stack>

            <Stack spacing={1}>
              <Typography variant="subtitle2" fontWeight="bold">
                Role
                <Box component="span" sx={{ color: `var(--error-700)` }}>
                  *
                </Box>
              </Typography>

              <Autocomplete
                multiple
                disabled={isArchivedUser || isDropdownDisabled || loadingRoles}
                getOptionDisabled={(option) => isRoleOptionDisabled(option)}
                options={roleAutocompleteOptions}
                value={roles}
                size="small"
                isOptionEqualToValue={(a, b) => a === b}
                getOptionLabel={(o) => o}
                filterOptions={filterRoleOptions}
                onChange={(_, v) => {
                  const next = sortRolesByPortalConfigOrder(
                    Array.from(
                      new Set([...lockedRoles, ...((v as Roles[]) || [])]),
                    ) as Roles[],
                  );
                  setRoles(next);
                  if (next.length > 0) clearError("roles");
                  handleChange(next, "roles", "Roles");
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Role"
                    required
                    error={!!errors.roles}
                    helperText={errors.roles || " "}
                  />
                )}
                renderTags={(value, getTagProps) => (
                  <Box
                    component="span"
                    sx={{ display: "inline-flex", flexWrap: "wrap", gap: 1 }}
                  >
                    {value.map((option, index) => {
                      const tagProps = getTagProps({ index });
                      const { key, onDelete, ...rest } = tagProps;
                      const isLocked = lockedRoles.includes(option);
                      return (
                        <Chip
                          key={key}
                          {...rest}
                          label={option}
                          onDelete={
                            isLocked
                              ? () =>
                                  showToast(
                                    "You don't have permissions to remove this role",
                                    "error",
                                  )
                              : onDelete
                          }
                          sx={{
                            backgroundColor: "white",
                            padding: "14px 6px",
                            borderRadius: "6px",
                          }}
                        />
                      );
                    })}
                  </Box>
                )}
                slotProps={{
                  popupIndicator: {
                    sx: { border: "none" },
                  },
                  clearIndicator: {
                    sx: { display: "none" },
                  },
                }}
              />
            </Stack>
          </Stack>
        </Stack>
        {isEditMode && (
          <Stack spacing={1}>
            <Typography variant="subtitle2" fontWeight="bold">
              Status
              <Box component="span" sx={{ color: `var(--error-700)` }}>
                *
              </Box>
            </Typography>
            <Select
              IconComponent={ExpandMoreIcon}
              displayEmpty
              value={status}
              onChange={(e) => setStatus(e.target.value as UserStatusType)}
              sx={{
                borderRadius: "8px",
                backgroundColor: "transparent",
                "&:hover": {
                  backgroundColor: "transparent",
                },
                "& .MuiSelect-select": {
                  display: "flex",
                  alignItems: "center",
                  padding: "8.5px 14px",
                },
              }}
            >
              {statusTypes.map((status, index) => (
                <MenuItem key={index} value={status}>
                  {status}
                </MenuItem>
              ))}
            </Select>
          </Stack>
        )}
        <Divider sx={{ my: 3 }} />
        {loadingAttributes ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {additionalAttributesResp.length > 0 && (
              <Typography variant="h5" fontWeight="bold" mb="1rem">
                Additional attributes
              </Typography>
            )}
            <Stack
              direction="row"
              flexWrap="wrap"
              useFlexGap
              justifyContent="space-between"
            >
              {visibleAttributes?.map(
                (attr: GetPortalUserAttributeResult, index: number) => (
                  <Box
                    key={
                      attr.portalUserAttributeUid || attr.attributeKey || index
                    }
                    flex="1 1 48%"
                    maxWidth="48%"
                  >
                    <DynamicAttributeField
                      attr={attr}
                      value={attributeValues[attr.attributeKey]}
                      setValue={setAttr}
                      error={errors[attr.attributeKey]}
                      disabled={isArchivedUser}
                      openFormDatepicker={
                        openFormDatepickerKey === attr.attributeKey
                      }
                      onOpenFormDatepicker={() =>
                        setOpenFormDatepickerKey(attr.attributeKey)
                      }
                      onCloseFormDatepicker={() =>
                        setOpenFormDatepickerKey(null)
                      }
                    />
                  </Box>
                ),
              )}
            </Stack>
            {additionalAttributesResp.length > ITEMS_PER_PAGE && (
              <Box display="flex" justifyContent="center" mt={2}>
                {visibleCount < additionalAttributesResp.length ? (
                  <IconButton
                    onClick={handleExpand}
                    sx={{
                      border: "none",
                      "&&:hover": { background: "transparent" },
                    }}
                  >
                    <ExpandMoreIcon />
                  </IconButton>
                ) : (
                  <IconButton
                    onClick={handleCollapse}
                    sx={{
                      border: "none",
                      "&&:hover": { background: "transparent" },
                    }}
                  >
                    <ExpandLessIcon />
                  </IconButton>
                )}
              </Box>
            )}
            {additionalAttributesResp.length > ITEMS_PER_PAGE && <Divider />}
          </>
        )}
        <Box width="100%" display="flex" justifyContent="center" mt={"2rem"}>
          {submitting ? (
            <Spinner size={40} fullscreen={false} height={40} />
          ) : (
            <Button
              sx={{
                px: 2,
                borderRadius: "12px",
                backgroundColor: `${brand[600]} !important`,
                color: `white`,
                fontWeight: "bold",
                "&:hover": {
                  bgcolor: `${brand[700]} !important`,
                  fontWeight: 700,
                },
              }}
              onClick={handleSubmit}
            >
              {isEditMode ? "Update user" : "Add user"}
            </Button>
          )}
        </Box>
        <ConfirmModal
          openConfirmModal={showArchivingModal}
          setOpenConfirmModal={(open) => {
            setShowArchivingModal(open);
            if (!open) setPendingArchiveSubmit(false);
          }}
          modalTitle="Archive users?"
          modalSubtitle="Are you sure you want to archive the selected users? They will no longer have access but can be restored later."
          confirmButtonClick={async () => {
            setShowArchivingModal(false);
            if (pendingArchiveSubmit) {
              setPendingArchiveSubmit(false);
              await submitUser();
            }
          }}
          confirmButtonText="Yes, I am sure"
          cancelButtonText="No, I don't want"
        />
        <Modal
          open={isPortalPreviewOpen}
          onClose={() => setIsPortalPreviewOpen(false)}
        >
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "90%",
              maxWidth: "600px",
              maxHeight: "90vh",
              overflowY: "auto",
              borderRadius: "16px",
              backgroundColor: gray[100],
              boxShadow: "0px 3px 6px rgba(0, 0, 0, 0.24)",
              p: 3,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography
                sx={{ fontSize: 18, fontWeight: 700, color: gray[900] }}
              >
                Assigned portals preview
              </Typography>
              <IconButton
                onClick={() => setIsPortalPreviewOpen(false)}
                sx={{ border: "none" }}
              >
                <CloseIcon sx={{ color: "#98A2B3" }} />
              </IconButton>
            </Box>
            <Divider sx={{ mt: 2, mb: 3, borderColor: gray[300] }} />
            <Stack spacing={3}>
              {(emailPortalMappings ?? []).map((portal) => (
                <Box
                  key={portal.portalUid}
                  sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}
                >
                  <Box
                    sx={{
                      width: 38,
                      height: 38,
                      borderRadius: "50%",
                      backgroundColor: "#E6E8EB",
                      border: "1px solid #D0D5DD",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <PortalIcon width={24} height={24} />
                  </Box>
                  <Box>
                    <Typography
                      sx={{ fontSize: 18, fontWeight: 600, color: gray[800] }}
                    >
                      {portal.name}
                    </Typography>
                    <Typography sx={{ fontSize: 14, color: gray[600] }}>
                      {portal.roles && portal.roles.length > 0
                        ? portal.roles.join(", ")
                        : "Assigned user"}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Stack>
          </Box>
        </Modal>
      </Paper>
    </Box>
  );
};
